import { ApplicationConfig, importProvidersFrom, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { catchError, firstValueFrom, of } from 'rxjs';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { provideRouter } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  HttpClient,
  provideHttpClient,
  withFetch,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { registerLocaleData } from '@angular/common';
import localeFa from '@angular/common/locales/fa';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { provideQueryClient, QueryClient } from '@tanstack/angular-query-experimental';
import { providePrimeNG } from 'primeng/config';
import { MessageService } from 'primeng/api';
import Aura from '@primeng/themes/aura';

import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { provideAuth } from 'angular-auth-oidc-client';

import { routes } from './app.routes';
import { hildaTranslateLoaderFactory } from './core/i18n/translate-loader.factory';
import { acceptLanguageInterceptor } from './core/i18n/accept-language.interceptor';
import { keycloakAuthConfig } from './core/auth/keycloak-auth.config';
import { keycloakBearerInterceptor } from './core/auth/keycloak-bearer.interceptor';
import { keycloak401Interceptor } from './core/auth/keycloak-401.interceptor';
import { devTenantInterceptor } from './core/auth/dev-tenant.interceptor';
import { actingAgencyInterceptor } from './core/auth/acting-agency.interceptor';
import { channelInterceptor } from './core/auth/channel.interceptor';
import { gatewayUrlInterceptor } from './core/http/gateway-url.interceptor';

// Jalali date formats — fed through jalali-moment via MomentDateAdapter.
const JALALI_DATE_FORMATS = {
  parse: {
    dateInput: 'jYYYY/jMM/jDD',
  },
  display: {
    dateInput: 'jYYYY/jMM/jDD',
    monthYearLabel: 'jMMMM jYYYY',
    dateA11yLabel: 'jLL',
    monthYearA11yLabel: 'jMMMM jYYYY',
  },
};

// Persian (fa) locale data — needed before any DatePipe / DecimalPipe /
// CurrencyPipe can format values with locale 'fa' or 'fa-IR'. Called once
// at bootstrap; the language switch effect (in AppService) flips LOCALE_ID
// at runtime so pipes pick up the new formatting without a reload.
registerLocaleData(localeFa, 'fa');

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimationsAsync(),

    // HTTP: functional + DI-based interceptors both supported.
    //
    // acceptLanguageInterceptor stamps every backend call with the current
    // UI language. It explicitly skips /assets/* so the ngx-translate bundle
    // load (which fires while AppService is still constructing) doesn't
    // create the inject(AppService).lang() cycle that would leave the
    // translate store empty.
    // Interceptor order matters:
    //   1. acceptLanguageInterceptor — stamps Accept-Language
    //   2. keycloakBearerInterceptor — attaches Authorization: Bearer
    //   3. devTenantInterceptor      — when environment.devTenant.enabled
    //      is true AND no Bearer token was attached (because dev users
    //      aren't logging in through Keycloak), stamps X-Tenant-* /
    //      X-Authenticated-* headers so the backend's dev bypass
    //      (DevTenantFilter) can populate TenantContext. Pure passthrough
    //      in production builds (environment.devTenant.enabled=false).
    //      Runs AFTER bearer so it can see whether bearer set Authorization.
    //   3b. actingAgencyInterceptor  — carries the INVENTORY acting-agency
    //      selection as X-Tenant-Agency on funnel calls for platform users.
    //      Separate from devTenantInterceptor on purpose: that one is gated on
    //      environment.devTenant.enabled, which is false in every environment
    //      file we ship, so the header was never sent and every platform-user
    //      booking was filed against the platform pseudo-agency instead of the
    //      agency on screen. Runs BEFORE the dev interceptor so a deliberate
    //      selection wins over any dev default.
    //   4. keycloak401Interceptor    — on the response stream, if a /api/* call
    //      returns 401 (token expired mid-session, revoked, etc.) it triggers
    //      a re-auth via oidc.authorize() so the user is bounced through
    //      Keycloak and back to the same page.
    provideHttpClient(
      withFetch(),
      // gatewayUrlInterceptor runs LAST — the other interceptors act on the
      // relative /api URL (attach bearer, dev headers, 401 handling); then, in
      // production only, it rewrites /api/* onto {apiBaseUrl}/<service>/api/*.
      withInterceptors([acceptLanguageInterceptor, keycloakBearerInterceptor, actingAgencyInterceptor, devTenantInterceptor, channelInterceptor, keycloak401Interceptor, gatewayUrlInterceptor]),
      withInterceptorsFromDi(),
    ),

    // angular-auth-oidc-client — Keycloak Authorization Code + PKCE flow.
    // See core/auth/keycloak-auth.config.ts for the realm/client/storage
    // choices. Exposed via /auth/keycloak (login button + claims sandbox)
    // and /auth/keycloak-callback (handles the redirect from Keycloak).
    provideAuth(keycloakAuthConfig),

    // Run checkAuth() before the router activates any route. The OIDC
    // library otherwise reports `isAuthenticated: false` on first visit
    // even when a valid token is sitting in localStorage — which would
    // make keycloakAuthGuard redirect to Keycloak, Keycloak send the user
    // back, callback set isAuthenticated=true, and a refresh restart the
    // loop. This initializer resolves the in-memory auth state from storage
    // (or processes a fresh `?code=…` callback) before any guard runs.
    //
    // Before checkAuth: drop stale `authWellKnownEndPoints` cached from a
    // previous run that pointed at a different Keycloak host. The lib reads
    // those endpoints on every `authorize()` instead of refetching, so a
    // moved authority (e.g. Keycloak 8080 → 8180) would silently keep
    // redirecting to the old URL until the user manually cleared storage.
    //
    // catchError ensures bootstrap NEVER hangs — if Keycloak is unreachable
    // the app still loads (with isAuthenticated=false) and the guard fires
    // its normal redirect when the user touches a protected route.
    provideAppInitializer(() => {
      const cfg = keycloakAuthConfig.config;
      const authority = Array.isArray(cfg) ? cfg[0]?.authority : cfg?.authority;
      purgeStaleOidcStorage(authority);
      return firstValueFrom(
        inject(OidcSecurityService).checkAuth().pipe(
          catchError(() => of({ isAuthenticated: false })),
        ),
      );
    }),

    // ngx-translate v17 — wired through TranslateModule.forRoot with our own
    // tiny HTTP loader (see translate-loader.factory.ts).
    //   default language = 'en'  (fallback for any missing key)
    //   loader            = HTTP, fetches /assets/i18n/{lang}.json?ts=…
    //                       (the ?ts cache-bust is critical: without it the
    //                        browser can cache an early empty placeholder and
    //                        every translate pipe falls through to the raw key)
    // AppService.setLang() drives the active language at runtime via
    // TranslateService.use(); see app.service.ts.
    importProvidersFrom(
      TranslateModule.forRoot({
        lang: 'en',
        fallbackLang: 'en',
        loader: {
          provide: TranslateLoader,
          useFactory: hildaTranslateLoaderFactory,
          deps: [HttpClient],
        },
      }),
    ),

    // PrimeNG: Aura preset, default ripple, modern CSS-vars driven theming.
    providePrimeNG({
      theme: { preset: Aura, options: { darkModeSelector: '[data-theme="dark"]' } },
      ripple: true,
    }),
    MessageService,

    // TanStack Query — for future HTTP layer.
    provideQueryClient(new QueryClient({
      defaultOptions: {
        queries: { staleTime: 30_000, refetchOnWindowFocus: false },
      },
    })),

    // Material datepicker on jalali-moment (jalali-moment is a moment.js superset).
    { provide: MAT_DATE_LOCALE, useValue: 'fa' },
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS] },
    { provide: MAT_DATE_FORMATS, useValue: JALALI_DATE_FORMATS },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: false } },
  ],
};

// angular-auth-oidc-client persists `<configId>_authWellKnownEndPoints` in
// localStorage on first successful discovery. If the configured authority
// later changes (e.g. dev Keycloak moves host/port), the lib keeps using the
// cached `authorization_endpoint` and never re-fetches — the user gets
// silently redirected to the old URL. Detect the mismatch on boot and wipe
// the OIDC-owned keys so the lib re-discovers from the live authority.
function purgeStaleOidcStorage(expectedAuthority: string | undefined): void {
  if (!expectedAuthority || typeof localStorage === 'undefined') return;
  try {
    const keys = Object.keys(localStorage).filter((k) => k.endsWith('_authWellKnownEndPoints'));
    for (const key of keys) {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const issuer = JSON.parse(raw)?.issuer ?? '';
      if (typeof issuer === 'string' && issuer && !issuer.startsWith(expectedAuthority)) {
        for (const k of Object.keys(localStorage)) {
          if (/^[0-9A-Za-z-]+_(authWellKnownEndPoints|authnResult|access_token|refresh_token|id_token|userData|authzData|authStateControl|session_state|storageSilentRenewRunning|storageCodeFlowInProgress|storageCodeVerifier|storageCustomParamsAuthRequest|jwtKeys|sessionState|nonce)$/.test(k)) {
            localStorage.removeItem(k);
          }
        }
        return;
      }
    }
  } catch {
    /* corrupt JSON / quota — leave storage alone, the lib will deal with it */
  }
}
